import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import '../video-basic/index.esm.js';
import '../types/index.esm.js';
import { getVideoId } from '../video-helper/index.esm.js';
import { EGDSVideoLayer } from '../video-layer/index.esm.js';
import '../video-layer-controls/index.esm.js';
import '@shared-ui/localization-context';
import '../../layout-position/types/index.esm.js';
import '../../layout-position/layout-position/index.esm.js';
import '../../loader/loader/index.esm.js';
import '../../loader/loader-container/index.esm.js';
import '../../loader/page-loader/index.esm.js';
import '../../scrim/scrim/index.esm.js';
import styles from '../../styles/uitk-egds-video.css';
import { useVideoHook } from '../video-custom-hook/index.esm.js';
import { ScriptLoader } from '../../utils/script-loader/index.esm.js';
import { classNames } from '../../utils/classnames/index.esm.js';
import { useIdCompatibility } from '../../utils/use-id-compatibility/index.esm.js';
const getClassNameList = classNames.bind(styles);
const EGDSEmbedVideo = props => {
  const {
    alt,
    analytics,
    className,
    isDialog,
    src,
    buttonText
  } = props;
  const {
    playerError,
    onPlayerError,
    isPlayerReady,
    onPlayerReady,
    playerState,
    onPlayerStateChange,
    player,
    setPlayer,
    onClickPlay,
    onClickPause
  } = useVideoHook(analytics);
  const videoId = getVideoId(src);
  const [playerVideoId, setPlayerVideoId] = React.useState(videoId);
  const customId = useIdCompatibility(); // Can replace with useId in React 18
  const uniqueIdRef = React.useRef(playerVideoId + customId);
  const wrapperVideoClassNames = getClassNameList('uitk-video', className);
  let playControlTriggered = false;
  /* istanbul ignore next */
  const createPlayer = () => {
    setPlayer(new window.YT.Player(`player-${videoId}${isDialog ? '-dialog' : ''}`, {
      events: {
        onError: onPlayerError,
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange
      },
      height: '100%',
      playerVars: {
        controls: 1,
        disablekb: 1,
        enablejsapi: 1,
        iv_load_policy: 3,
        modestbranding: 1,
        rel: 0
      },
      videoId,
      width: '100%'
    }));
  };
  React.useEffect(() => {
    if (!window.RunOnYouTubeIframeAPIReady) {
      window.RunOnYouTubeIframeAPIReady = new Map();
    }
    if (!window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => {
        // Dequeue and run all functions in the map
        while (window.RunOnYouTubeIframeAPIReady.size) {
          window.RunOnYouTubeIframeAPIReady.forEach((fn, key) => {
            fn();
            window.RunOnYouTubeIframeAPIReady.delete(key);
          });
        }
      };
    }
    // YT could be available but YT.Player isn't available yet, especially on a Slow network.
    // YT.Player is added by a different script (www-widgetapi.js) than window.YT
    /* istanbul ignore next */
    if (!window.YT) {
      ScriptLoader.getScript(`https://www.youtube.com/iframe_api`).catch(console.error);
      window.RunOnYouTubeIframeAPIReady.set(uniqueIdRef.current, createPlayer);
    } else if (!window.YT.Player) {
      window.RunOnYouTubeIframeAPIReady.set(uniqueIdRef.current, createPlayer);
    } else if (!player && !window.RunOnYouTubeIframeAPIReady.has(uniqueIdRef.current)) {
      createPlayer();
    } else if (player && videoId !== playerVideoId && isPlayerReady) {
      setPlayerVideoId(videoId);
      player.cueVideoById(videoId);
    }
  });
  if (typeof document === 'undefined') {
    return null;
  }
  return jsxs("div", {
    className: wrapperVideoClassNames,
    children: [jsx(EGDSVideoLayer, {
      alt: alt || '',
      controls: {
        pause: onClickPause,
        play: () => {
          if (window && !playControlTriggered) {
            // Necessary a11y fix to prevent the Youtube Iframe player's controls from being tabbable before users trigger the EGDSVideo controls
            const youtubeIframeElement = window.document?.getElementById(`player-${videoId}${isDialog ? '-dialog' : ''}`);
            youtubeIframeElement?.setAttribute('tabIndex', '0');
            playControlTriggered = true;
          }
          onClickPlay();
        }
      },
      buttonText: buttonText,
      isPlayerReady: isPlayerReady,
      playerError: playerError,
      playerState: playerState,
      src: src
    }), jsx("div", {
      tabIndex: -1,
      id: `player-${videoId}${isDialog ? '-dialog' : ''}`
    })]
  });
};
export { EGDSEmbedVideo };